import React from 'react';
import { map, findIndex, includes, isEmpty, isNil } from 'lodash';
import classnames from 'classnames';
import { Menu, MenuItem, Collapse } from '@mui/material';
import { AddCircle as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import SchemaValidationItemInfo from './SchemaValidationItemInfo';
import {
  SelectInput,
  SelectOption,
  getNewSelectionValue,
  getCurrentSelectedOption,
} from '../../rselect/ReactSelectUtils';
import SortedSelect from '../../rselect/SortedSelect';
import UnsortedSelect from '../../rselect/UnsortedSelect';

import {
  ValidationTypeOptions,
  ValidationTypes,
  ValidationHeadings,
} from 'js/constants/schemaEditor';
import { VALIDATION_FORMAT_OPTIONS } from 'js/constants/attributeFormats';
import { TextField } from 'new/elements/TextField';
/**
 * SVG icons
 */
import { ReactComponent as ExpandIcon } from 'new/components/icons/unfoldmore-outlined.svg';
import { ReactComponent as RoundedDeleteIcon } from 'new/components/icons/close-cross-filled-round.svg';

import './SchemaValidationsEditor.scss';
import { RemoveIconButton } from 'new/elements/Button/RemoveIconButton';

class SchemaValidationsEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      newEnumValue: '',
    };

    this._renderValidation = this._renderValidation.bind(this);
    this._renderExpandedValidationContent = this._renderExpandedValidationContent.bind(this);
    this._renderAttrSelector = this._renderAttrSelector.bind(this);
    this._handleEmptyAreaClicked = this._handleEmptyAreaClicked.bind(this);
    this._handleValidationClicked = this._handleValidationClicked.bind(this);
    this._handleAddValidationClicked = this._handleAddValidationClicked.bind(this);
    this._handleRemoveValidationClicked = this._handleRemoveValidationClicked.bind(this);
    this._handleCloseNewValidationPopOver = this._handleCloseNewValidationPopOver.bind(this);
    this._handleNewValidationSelected = this._handleNewValidationSelected.bind(this);
    this._handleAttrSelected = this._handleAttrSelected.bind(this);
    this._handleOptionChanged = this._handleOptionChanged.bind(this);
    this._handleRemoveAttrClicked = this._handleRemoveAttrClicked.bind(this);
    this._handleAddFormat = this._handleAddFormat.bind(this);
    this._handleRemoveFormatClicked = this._handleRemoveFormatClicked.bind(this);
    this._handleEnumValueKeyDown = this._handleEnumValueKeyDown.bind(this);
    this._handleNewEnumValueChanged = this._handleNewEnumValueChanged.bind(this);
    this._handleAddEnumValueClicked = this._handleAddEnumValueClicked.bind(this);
    this._handleRemoveEnumValueClicked = this._handleRemoveEnumValueClicked.bind(this);
  }

  render() {
    const { data, isReadOnly, isExpanded } = this.props;
    const { anchorEl } = this.state;

    return (
      <div
        data-ignore-bubbling-click={true}
        className="schema-validations-editor"
        onClick={this._handleEmptyAreaClicked}
      >
        <div
          data-ignore-bubbling-click={true}
          className="schema-validations-editor-content"
          onClick={this._handleEmptyAreaClicked}
        >
          <Collapse in={isExpanded}>
            <ul className="filters-list">{map(data, this._renderValidation)}</ul>
          </Collapse>
          {!isReadOnly && (
            <div data-ignore-bubbling-click={true} onClick={this._handleEmptyAreaClicked}>
              <button className="add-recipe-button" onClick={this._handleAddValidationClicked}>
                + Add New Validation
              </button>
              <Menu
                variant="menu"
                open={!!anchorEl}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={this._handleCloseNewValidationPopOver}
              >
                {map(ValidationTypeOptions, ({ label, value }) => (
                  <MenuItem
                    key={value}
                    onClick={(e) => {
                      e.stopPropagation();
                      this._handleNewValidationSelected(value);
                    }}
                    style={{ minHeight: 'unset', fontSize: 'var(--font-size-base-sm)', width: 112 }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}
        </div>
      </div>
    );
  }

  _renderValidation(validation, index) {
    const { expandedRecipeIndex, isReadOnly, isInteractive } = this.props;
    const { type } = validation;
    const isExpanded = !isReadOnly && expandedRecipeIndex === index;

    return (
      <li
        key={index}
        data-index={index}
        onClick={this._handleValidationClicked}
        className={classnames('filter-item', {
          'is-focused': isExpanded,
          'is-blurred': !isExpanded,
          'is-read-only': isReadOnly && !isInteractive,
        })}
      >
        <div className="left">
          <div className="fi-header" onClick={this._handleValidationClicked}>
            <h3>{ValidationHeadings[type]}</h3>
            {!isReadOnly && (
              <div className="actions">
                <ExpandIcon
                  role="button"
                  width={16}
                  height={16}
                  onClick={this._handleValidationClicked}
                />

                <RemoveIconButton
                  onClickRemove={this._handleRemoveValidationClicked}
                  tooltipText="Remove Rule"
                  className="delete-icon"
                  additionalProps={{
                    'data-index': index,
                  }}
                />
              </div>
            )}
          </div>

          {isExpanded ? (
            this._renderExpandedValidationContent(validation, index)
          ) : (
            <SchemaValidationItemInfo validation={validation} />
          )}
        </div>
      </li>
    );
  }

  _renderExpandedValidationContent(validation, index) {
    const { type, options } = validation;

    switch (type) {
      case ValidationTypes.REQUIRED:
        return this._renderAttrSelector(validation, index, 'Record must contain:');

      case ValidationTypes.FORMAT:
        return (
          <div className="gap">
            <label>Attributes</label>
            {this._renderAttrSelector(validation, index)}
            <label>must be of type</label>
            <UnsortedSelect
              placeholder="Select format..."
              options={VALIDATION_FORMAT_OPTIONS}
              value={null}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={false}
              backspaceRemovesValue={false}
              variant="default"
              onChange={(selection) =>
                this._handleAddFormat(getNewSelectionValue(selection, true), index)
              }
            />

            {!isEmpty(options.formats) && (
              <ul className="attr-list">
                {map(options.formats, (format, formatIndex) => (
                  <li key={formatIndex}>
                    <RoundedDeleteIcon
                      width={16}
                      height={16}
                      data-index={index}
                      data-format-index={formatIndex}
                      onClick={this._handleRemoveFormatClicked}
                    />

                    <span className="attr-name">
                      {getCurrentSelectedOption(VALIDATION_FORMAT_OPTIONS, format).label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case ValidationTypes.RANGE:
      case ValidationTypes.SIZE:
        return (
          <div className="gap">
            <label>Attributes</label>
            {this._renderAttrSelector(validation, index)}
            <label>
              {type === ValidationTypes.RANGE ? 'must be between' : 'must have size between'}
            </label>
            <TextField
              fullWidth
              style={{ background: 'white' }}
              value={options.min || ''}
              placeholder="Minimum value"
              onChange={(event) => this._handleOptionChanged(index, 'min', event.target.value)}
            />

            <label>and</label>
            <TextField
              fullWidth
              style={{ background: 'white' }}
              value={options.max || ''}
              placeholder="Maximum value"
              onChange={(event) => this._handleOptionChanged(index, 'max', event.target.value)}
            />
          </div>
        );

      case ValidationTypes.ENUM:
        const { newEnumValue } = this.state;

        return (
          <div className="gap">
            <label>Attributes</label>
            {this._renderAttrSelector(validation, index)}
            <label>must have one of these values:</label>
            <div className="new-enum-value-container">
              <TextField
                fullWidth
                style={{ background: 'white' }}
                data-index={index}
                value={newEnumValue}
                placeholder="Enter a value"
                inputProps={{ 'data-index': index }}
                onKeyDown={this._handleEnumValueKeyDown}
                onChange={this._handleNewEnumValueChanged}
              />

              <AddIcon
                data-index={index}
                fontSize="large"
                className="add-enum-value-button"
                onClick={this._handleAddEnumValueClicked}
              />
            </div>
            {!isEmpty(options.values) && (
              <ul className="attr-list">
                {map(options.values, (value, valueIndex) => (
                  <li key={valueIndex}>
                    <RoundedDeleteIcon
                      data-index={index}
                      data-value-index={valueIndex}
                      width={16}
                      height={16}
                      onClick={this._handleRemoveEnumValueClicked}
                    />

                    <span className="attr-name">{value}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case ValidationTypes.PATTERN:
        return (
          <div className="gap">
            <label>Attributes</label>
            {this._renderAttrSelector(validation, index)}
            <label>must match the pattern</label>
            <TextField
              fullWidth
              value={options.pattern}
              placeholder="Regular expression"
              style={{ background: 'white' }}
              onChange={(event) => this._handleOptionChanged(index, 'pattern', event.target.value)}
            />
          </div>
        );

      default:
    }
  }

  _renderAttrSelector(validation, index, listLabel = null) {
    const { data, toFlat } = this.props;
    const { attrs } = validation;

    const selectOptions = map(toFlat, ({ __name }) => ({
      label: __name,
      value: __name,
    }));

    return (
      <div>
        <SortedSelect
          isCreatable={true}
          placeholder="Select or type output attributes..."
          noOptionsMessage={() =>
            "Don't see the attribute you want? Type and press enter to add it."
          }
          options={selectOptions}
          isOptionDisabled={(option) =>
            // disable selection if this attr has already been used in a
            // validation of the same type
            findIndex(data, (v) => v.type === validation.type && includes(v.attrs, option.value)) >
            -1
          }
          formatCreateLabel={(value) => value}
          value={null}
          components={{
            Input: SelectInput,
            Option: SelectOption,
            DropdownIndicator: () => <SearchIcon style={{ color: '#ccc' }} />,
          }}
          isClearable={false}
          backspaceRemovesValue={false}
          variant="default"
          onChange={(selection) => this._handleAttrSelected(index, selection)}
        />

        {!isEmpty(attrs) && (
          <div>
            {listLabel && <label className="attr-list-label">{listLabel}</label>}
            <ul className="attr-list">
              {map(attrs, (attr, attrIndex) => (
                <li key={attr?.toString()}>
                  <RoundedDeleteIcon
                    width={16}
                    height={16}
                    data-index={index}
                    data-attr-index={attrIndex}
                    onClick={this._handleRemoveAttrClicked}
                  />

                  <span className="attr-name">{attr}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  _handleEmptyAreaClicked(event) {
    const { setExpandedRecipe } = this.props;
    const { ignoreBubblingClick } = event.currentTarget.dataset;

    if (!ignoreBubblingClick || event.currentTarget === event.target) {
      setExpandedRecipe(-1);
    }
  }

  _handleValidationClicked(event) {
    const { expandedRecipeIndex, isReadOnly, isInteractive, setExpandedRecipe, onRecipeClicked } =
      this.props;
    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const isExpanded = expandedRecipeIndex === index;

    if (isReadOnly && isInteractive) {
      onRecipeClicked(index);
    }

    if (!isReadOnly && !isExpanded) {
      setExpandedRecipe(index);
    }
  }

  _handleAddValidationClicked(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  _handleCloseNewValidationPopOver() {
    this.setState({
      anchorEl: null,
    });
  }

  _handleNewValidationSelected(type) {
    const { data, isExpanded, setExpandedRecipe, onExpand, onChangeRecipeGroup } = this.props;
    const newData = [
      ...data,
      {
        type,
        attrs: [],
        options: {},
      },
    ];

    if (!isExpanded) {
      onExpand();
    }

    onChangeRecipeGroup(newData);
    setExpandedRecipe(data.length);
    this._handleCloseNewValidationPopOver();
  }

  _handleRemoveValidationClicked(event) {
    event.stopPropagation();

    const { data, onChangeRecipeGroup } = this.props;
    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const newData = [...data];
    newData.splice(index, 1);
    onChangeRecipeGroup(newData);
  }

  _handleAttrSelected(index, selection) {
    const { data, onChangeRecipeGroup } = this.props;
    const validation = data[index];
    const attr = getNewSelectionValue(selection, true);

    if (includes(validation.attrs, attr)) {
      return;
    }

    const newValidation = {
      ...validation,
      attrs: [...validation.attrs, attr],
    };
    const newData = [...data];
    newData[index] = newValidation;
    onChangeRecipeGroup(newData);
  }

  _handleOptionChanged(index, name, value) {
    const { data, onChangeRecipeGroup } = this.props;
    const validation = data[index];

    if (validation.options[name] === value) {
      return;
    }

    const newValidation = {
      ...validation,
      options: {
        ...validation.options,
        [name]: value,
      },
    };
    const newData = [...data];
    newData[index] = newValidation;
    onChangeRecipeGroup(newData);
  }

  _handleRemoveAttrClicked(event) {
    const { data, onChangeRecipeGroup } = this.props;
    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const attrIndex = parseInt(dataset.attrIndex, 10);
    const validation = data[index];
    const attrs = [...validation.attrs];
    attrs.splice(attrIndex, 1);
    const newValidation = {
      ...validation,
      attrs,
    };
    const newData = [...data];
    newData[index] = newValidation;
    onChangeRecipeGroup(newData);
  }

  _handleAddFormat(format, index) {
    const { data } = this.props;
    const validation = data[index];
    const formats = [...(validation.options.formats || [])];

    if (includes(formats, format)) {
      return;
    }

    formats.push(format);

    this._handleOptionChanged(index, 'formats', formats);
  }

  _handleRemoveFormatClicked(event) {
    const { data } = this.props;
    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const formatIndex = parseInt(dataset.formatIndex, 10);
    const validation = data[index];
    const formats = [...(validation.options.formats || [])];
    formats.splice(formatIndex, 1);

    this._handleOptionChanged(index, 'formats', formats);
  }

  _handleEnumValueKeyDown(event) {
    if (event.keyCode === 13) {
      this._handleAddEnumValueClicked(event);
    }
  }

  _handleNewEnumValueChanged(event) {
    this.setState({ newEnumValue: event.target.value });
  }

  _handleAddEnumValueClicked(event) {
    const { data } = this.props;
    const { newEnumValue } = this.state;
    // const effectiveValue = //newEnumValue.trim();

    if (isNil(newEnumValue)) {
      return;
    }

    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const validation = data[index];
    const values = [...(validation.options.values || [])];

    if (includes(values, newEnumValue)) {
      return;
    }

    values.push(newEnumValue);

    this._handleOptionChanged(index, 'values', values);
    this.setState({ newEnumValue: '' });
  }

  _handleRemoveEnumValueClicked(event) {
    const { data } = this.props;
    const { dataset } = event.currentTarget;
    const index = parseInt(dataset.index, 10);
    const valueIndex = parseInt(dataset.valueIndex, 10);
    const validation = data[index];
    const values = [...(validation.options.values || [])];
    values.splice(valueIndex, 1);

    this._handleOptionChanged(index, 'values', values);
  }
}

export default SchemaValidationsEditor;
